import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../lib/realtime';
import { canonicalMoney, sumMoney } from '../../lib/money';
import type { Service } from '../../types';

async function log(biz: string, type: string, desc: string, eId: string, uid: string) {
  try { await supabase.from('activity_log').insert({ business_id: biz, activity_type: type, description: desc, entity_type: 'service', entity_id: eId, logged_by: uid }); } catch { /* */ }
}

export interface ServiceFilters { category: string | null; search: string; }

export interface ServiceSummary {
  activeCount: number;
  avgPrice: number;
  avgDuration: number;
  topBooked: { name: string; count: number }[];
}

export function useServices(businessId: string | undefined) {
  const [all, setAll] = useState<Service[]>([]);
  const [bookingCounts, setBookingCounts] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ServiceFilters>({ category: null, search: '' });
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true); setError(null);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000);
    try {
      const [svcRes, bookRes] = await Promise.all([
        supabase.from('services').select('*').eq('business_id', businessId)
          .order('sort_order').order('name').abortSignal(ctrl.signal),
        supabase.from('bookings').select('service_id').eq('business_id', businessId)
          .not('service_id', 'is', null).abortSignal(ctrl.signal),
      ]);
      if (svcRes.error) throw svcRes.error;
      const countMap = new Map<string, number>();
      for (const b of bookRes.data ?? []) {
        if (b.service_id) countMap.set(b.service_id, (countMap.get(b.service_id) ?? 0) + 1);
      }
      if (mountedRef.current) { setAll((svcRes.data ?? []) as Service[]); setBookingCounts(countMap); }
    } catch (e) { if (mountedRef.current) setError(e instanceof Error ? e.message : 'Failed'); }
    finally { clearTimeout(t); if (mountedRef.current) setLoading(false); }
  }, [businessId]);

  useEffect(() => { mountedRef.current = true; void fetch(); return () => { mountedRef.current = false; }; }, [fetch]);
  const rt = useMemo(() => ['services'], []);
  useRealtimeSubscription(businessId, rt, fetch);

  const filtered = useMemo(() => {
    let list = all;
    if (filters.category) list = list.filter(s => s.category === filters.category);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(q));
    }
    return list;
  }, [all, filters]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const s of all) { if (s.category) set.add(s.category); }
    return Array.from(set).sort();
  }, [all]);

  const summary = useMemo<ServiceSummary>(() => {
    const active = all.filter(s => s.is_active !== false);
    const prices = active.filter(s => s.price != null).map(s => s.price!);
    const durations = active.filter(s => s.duration_minutes != null).map(s => s.duration_minutes!);
    const topBooked = all
      .map(s => ({ name: s.name, count: bookingCounts.get(s.id) ?? 0 }))
      .filter(x => x.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
    return {
      activeCount: active.length,
      // Rule #34: canonical money sum / count. Number() at consumer boundary.
      avgPrice: prices.length ? Math.round(Number(sumMoney(prices.map(p => canonicalMoney(p)))) / prices.length) : 0,
      avgDuration: durations.length ? Math.round(durations.reduce((s, d) => s + d, 0) / durations.length) : 0,
      topBooked,
    };
  }, [all, bookingCounts]);

  const createService = useCallback(async (data: Partial<Service>, userId: string): Promise<string | null> => {
    if (!businessId) return null;
    try {
      const { data: svc, error: e } = await supabase.from('services').insert({
        ...data, business_id: businessId, is_active: true, sort_order: data.sort_order ?? all.length + 1,
      }).select('id').maybeSingle();
      if (e || !svc) return null;
      await log(businessId, 'service_created', `Service created: ${data.name}`, svc.id, userId);
      void fetch(); return svc.id;
    } catch { return null; }
  }, [businessId, all.length, fetch]);

  const updateService = useCallback(async (id: string, data: Partial<Service>, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('services').update(data).eq('id', id).eq('business_id', businessId);
      if (e) return false;
      await log(businessId, 'service_updated', `Service updated: ${data.name ?? id}`, id, userId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  const toggleActive = useCallback(async (id: string, isActive: boolean, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('services').update({ is_active: !isActive }).eq('id', id).eq('business_id', businessId);
      if (e) return false;
      await log(businessId, isActive ? 'service_deactivated' : 'service_activated', `Service ${isActive ? 'deactivated' : 'activated'}`, id, userId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  return { services: filtered, allServices: all, categories, loading, error, summary, filters, setFilters, createService, updateService, toggleActive, refresh: fetch };
}
